import { useEffect, useMemo, useRef, useState } from 'react'
import { APP_CONFIG } from '@config/constants'
import { CHART_THEME, DATA_SERIES_COLORS, VISUAL_COLORS } from '@config/visualTheme'
import { useAuth } from '@context/AuthContext'
import { useTheme } from '@context/ThemeContext'
import {
  exportPortalReport,
  getPortalClientRequests,
  getPortalDashboard,
  getPortalProcesses,
  requestPortalAccess,
} from '@services/portalService'
import {
  ReportChartCard,
  ReportEmptyState,
  ReportStat,
  formatReportCurrency,
  formatReportDate,
  formatReportNumber,
} from '@components/shared/reports/ReportPrimitives'
import ThemeToggle from '@components/shared/ThemeToggle'
import { PORTAL_BRAND } from '@config/brand'
import PortalBrand from './PortalBrand'
import PortalHearingsPanel from './PortalHearingsPanel'
import PortalRegionFilters from './PortalRegionFilters'
import {
  PortalClientRequestsPanel,
  PortalDocumentsPanel,
  PortalProcessDetailDrawer,
} from './PortalWorkspace'
import './portal.css'

const EMPTY_ACCESS_FORM = {
  nome: '', email: '', empresa: '', telefone: '', cargo: '', mensagem: '', website: '',
}

const EMPTY_PROCESS_FILTERS = { estado: '', comarca: '', busca: '' }

function formatPortalDateTime(value) {
  if (!value) return 'Não informada'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Não informada' : new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(date)
}

function formatPhoneNumber(value) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  if (!digits) return ''
  if (digits.length <= 2) return `(${digits}`

  const areaCode = digits.slice(0, 2)
  const number = digits.slice(2)
  if (number.length <= 4) return `(${areaCode}) ${number}`
  if (number.length <= 8) return `(${areaCode}) ${number.slice(0, 4)}-${number.slice(4)}`
  return `(${areaCode}) ${number.slice(0, 5)}-${number.slice(5)}`
}

function TurnstileWidget({ onToken }) {
  const { isDarkMode } = useTheme()
  const hostRef = useRef(null)
  const widgetIdRef = useRef(null)

  useEffect(() => {
    if (!APP_CONFIG.TURNSTILE_SITE_KEY || !hostRef.current) return undefined
    let disposed = false

    const renderWidget = () => {
      if (disposed || !hostRef.current || !window.turnstile || widgetIdRef.current !== null) return
      widgetIdRef.current = window.turnstile.render(hostRef.current, {
        sitekey: APP_CONFIG.TURNSTILE_SITE_KEY,
        action: 'portal_access_request',
        theme: isDarkMode ? 'dark' : 'light',
        size: 'flexible',
        callback: (token) => onToken(token),
        'expired-callback': () => onToken(''),
        'error-callback': () => onToken(''),
      })
    }

    const existing = document.querySelector('script[data-veiga-turnstile="true"]')
    if (existing) {
      if (window.turnstile) renderWidget()
      else existing.addEventListener('load', renderWidget, { once: true })
    } else {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
      script.async = true
      script.defer = true
      script.dataset.veigaTurnstile = 'true'
      script.addEventListener('load', renderWidget, { once: true })
      document.head.appendChild(script)
    }

    return () => {
      disposed = true
      onToken('')
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current) } catch { /* widget já removido */ }
      }
      widgetIdRef.current = null
    }
  }, [isDarkMode, onToken])

  if (!APP_CONFIG.TURNSTILE_SITE_KEY) {
    return <div className="portal-turnstile-warning">A verificação de segurança ainda não foi configurada neste ambiente.</div>
  }
  return <div className="portal-turnstile" ref={hostRef} aria-label="Verificação anti-robô" />
}

function AccessRequestForm({ onBack }) {
  const [form, setForm] = useState(EMPTY_ACCESS_FORM)
  const [turnstileToken, setTurnstileToken] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [sent, setSent] = useState(false)
  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }))

  const submit = async (event) => {
    event.preventDefault()
    setError('')
    if (!turnstileToken) {
      setError('Conclua a verificação de segurança para enviar a solicitação.')
      return
    }
    setSubmitting(true)
    const result = await requestPortalAccess({ ...form, turnstileToken })
    setSubmitting(false)
    if (!result.success) {
      setError(result.error || 'Não foi possível enviar sua solicitação.')
      return
    }
    setSent(true)
  }

  if (sent) {
    return (
      <div className="portal-auth-card portal-access-success">
        <PortalBrand compact className="portal-auth-card-brand" />
        <div className="portal-success-icon"><i className="fas fa-check" /></div>
        <span className="portal-eyebrow">Solicitação recebida</span>
        <h1>Obrigado pelo contato.</h1>
        <p>Nossa equipe irá validar seu vínculo com a empresa informada. Se o acesso for aprovado, você receberá as instruções no e-mail cadastrado.</p>
        <button type="button" className="portal-secondary-button" onClick={onBack}>Voltar para o login</button>
      </div>
    )
  }

  return (
    <div className="portal-auth-card portal-request-card">
      <PortalBrand compact className="portal-auth-card-brand" />
      <button type="button" className="portal-back-button" onClick={onBack}><i className="fas fa-arrow-left" /> Voltar</button>
      <span className="portal-eyebrow">Novo acesso</span>
      <h1>Solicitar acesso</h1>
      <p>Preencha seus dados profissionais. O acesso só será liberado após validação da equipe Veiga.</p>
      <form onSubmit={submit} className="portal-access-form">
        <div className="portal-form-grid">
          <label className="portal-field full"><span>Nome completo</span><input value={form.nome} onChange={(e) => update('nome', e.target.value)} required minLength={3} maxLength={180} autoComplete="name" /></label>
          <label className="portal-field full"><span>E-mail profissional</span><input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} required maxLength={255} autoComplete="email" /></label>
          <label className="portal-field full"><span>Empresa</span><input value={form.empresa} onChange={(e) => update('empresa', e.target.value)} required minLength={2} maxLength={220} autoComplete="organization" /></label>
          <label className="portal-field"><span>Telefone</span><input value={form.telefone} onChange={(e) => update('telefone', formatPhoneNumber(e.target.value))} maxLength={15} inputMode="tel" autoComplete="tel" placeholder="(00) 00000-0000" /></label>
          <label className="portal-field"><span>Cargo</span><input value={form.cargo} onChange={(e) => update('cargo', e.target.value)} maxLength={120} autoComplete="organization-title" /></label>
          <label className="portal-field full"><span>Mensagem <small>opcional</small></span><textarea value={form.mensagem} onChange={(e) => update('mensagem', e.target.value)} maxLength={1500} rows={3} placeholder="Se necessário, conte por que precisa acessar o portal." /></label>
          <label className="portal-honeypot" aria-hidden="true">Website<input tabIndex={-1} autoComplete="off" value={form.website} onChange={(e) => update('website', e.target.value)} /></label>
        </div>
        <TurnstileWidget onToken={setTurnstileToken} />
        {error ? <div className="portal-form-error"><i className="fas fa-circle-exclamation" /> {error}</div> : null}
        <button type="submit" className="portal-primary-button" disabled={submitting || !APP_CONFIG.TURNSTILE_SITE_KEY}>
          {submitting ? <><i className="fas fa-spinner fa-spin" /> Enviando...</> : 'Enviar solicitação'}
        </button>
        <p className="portal-form-note">Para sua segurança, este formulário possui proteção anti-robô e limite de solicitações.</p>
      </form>
    </div>
  )
}

function PortalLogin() {
  const { login } = useAuth()
  const [requestingAccess, setRequestingAccess] = useState(false)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    document.body.classList.add('portal-login-active')

    return () => document.body.classList.remove('portal-login-active')
  }, [])

  const submit = async (event) => {
    event.preventDefault()
    setError('')
    setLoading(true)
    const result = await login(email.trim(), password)
    setLoading(false)
    if (!result.success) setError(result.error || 'Não foi possível entrar.')
  }

  return (
    <main className="client-portal portal-auth-page portal-login-experience">
      <div className="portal-login-technical-grid" aria-hidden="true" />

      <section className="portal-auth-branding" aria-labelledby="portal-login-story-title">
        <div className="portal-auth-art" aria-hidden="true">
          <img src={PORTAL_BRAND.artworkPath} alt="" />
        </div>

        <p className="portal-login-eyebrow"><span /> Inteligência aplicada</p>
        <div className="portal-auth-message">
          <h1 id="portal-login-story-title">Sua carteira jurídica em uma <em>visão executiva.</em></h1>
          <p>Performance, exposição, custas e movimentações conectadas para decisões mais seguras.</p>
        </div>

        <div className="portal-login-route" aria-label="Carteira, risco e decisão">
          <span>Carteira</span><i /><span>Risco</span><i /><span>Decisão</span>
        </div>

        <dl className="portal-login-pillars">
          <div>
            <dt>01</dt>
            <dd><strong>Performance</strong><span>Movimentações e indicadores visíveis.</span></dd>
          </div>
          <div>
            <dt>02</dt>
            <dd><strong>Exposição</strong><span>Custas e provisões organizadas.</span></dd>
          </div>
          <div>
            <dt>03</dt>
            <dd><strong>Evidência</strong><span>Decisões apoiadas por dados.</span></dd>
          </div>
        </dl>
      </section>

      <section className="portal-auth-panel">
        <div className={`portal-auth-surface ${requestingAccess ? 'is-requesting' : ''}`}>
          <div className="portal-auth-theme-toggle">
            <ThemeToggle variant="login" />
          </div>

          {requestingAccess ? <AccessRequestForm onBack={() => setRequestingAccess(false)} /> : (
            <div className="portal-auth-card portal-login-card">
              <div className="portal-login-card-brand">
                <h2>{PORTAL_BRAND.stem} <span>{PORTAL_BRAND.accent}</span></h2>
                <p>{PORTAL_BRAND.tagline}</p>
              </div>

              <p className="portal-login-access-note"><span /> Acesso exclusivo para clientes autorizados</p>

              <form className="portal-login-form" onSubmit={submit}>
                <label className="portal-field">
                  <span><b>01</b> E-mail profissional</span>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" required placeholder="voce@empresa.com" />
                </label>
                <label className="portal-field">
                  <span><b>02</b> Senha de acesso</span>
                  <div className="portal-password-field">
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" required placeholder="Digite sua senha" />
                    <button type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}><i className={`far ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`} /></button>
                  </div>
                </label>
                {error ? <div className="portal-form-error"><i className="fas fa-circle-exclamation" /> {error}</div> : null}
                <button className="portal-primary-button" type="submit" disabled={loading}>
                  {loading ? <><i className="fas fa-spinner fa-spin" /> Entrando...</> : <><i className="fas fa-arrow-right-to-bracket" /> Acessar carteira</>}
                </button>
              </form>

              <div className="portal-auth-divider"><span>Primeiro acesso?</span></div>
              <button type="button" className="portal-request-access" onClick={() => setRequestingAccess(true)}>Solicitar acesso à ferramenta <i className="fas fa-arrow-right" /></button>

              <p className="portal-login-security-note">
                <i className="fas fa-shield-halved" aria-hidden="true" /> Ambiente seguro e monitorado
              </p>
            </div>
          )}
        </div>
      </section>
    </main>
  )
}

function PortalAccessDenied() {
  const { logout } = useAuth()
  return (
    <main className="client-portal portal-centered-page"><div className="portal-auth-card portal-denied-card"><PortalBrand compact /><div className="portal-neutral-icon"><i className="fas fa-lock" /></div><h1>Acesso ao portal não disponível</h1><p>Esta conta não possui uma empresa vinculada ao Adjutorem IA. Se você faz parte da equipe Veiga, utilize o ambiente de gestão.</p><a className="portal-primary-button as-link" href={APP_CONFIG.INTERNAL_URL}>Ir para a HarmonIA</a><button type="button" className="portal-link-button" onClick={logout}>Sair desta conta</button></div></main>
  )
}

function PortalProcesses({ page, loading, error, onRetry, onPageChange, onProcessSelect }) {
  return (
    <section className="portal-section portal-process-section">
      <div className="portal-section-heading">
        <div><span className="portal-eyebrow">Carteira processual</span><h2>Seus processos</h2></div>
        {!loading && !error && page ? <span className="portal-result-count">{formatReportNumber(page.total)} processos encontrados</span> : null}
      </div>
      {loading ? <div className="portal-inline-loading" role="status"><i className="fas fa-spinner fa-spin" /> Carregando processos...</div> : error ? (
        <div className="portal-form-error portal-retry-error" role="alert"><span>{error}</span><button type="button" className="portal-secondary-button" onClick={onRetry}>Tentar novamente</button></div>
      ) : (
        <div className="portal-table-wrap"><table className="portal-process-table"><thead><tr><th>Processo</th><th>Área / objeto</th><th>Região</th><th>Status</th><th>Perspectiva</th><th>Último andamento</th></tr></thead><tbody>
          {page?.itens?.length ? page.itens.map((item) => (
            <tr key={item.processoId} className="portal-process-clickable" onClick={() => onProcessSelect(item.processoId)}>
              <td><strong>{item.numeroProcesso || `#${item.processoId}`}</strong><small>{item.partePrincipal || 'Parte não informada'} × {item.parteContraria || 'Parte não informada'}</small><button type="button" className="portal-process-open portal-process-detail-link" onClick={(event) => { event.stopPropagation(); onProcessSelect(item.processoId) }} aria-label={`Ver detalhes do processo ${item.numeroProcesso || item.processoId}`}>Ver detalhes <i className="fas fa-arrow-right" /></button></td>
              <td><strong>{item.area || 'Não informada'}</strong><small>{item.objeto || 'Objeto não informado'}</small></td>
              <td><strong>{item.estado || 'Estado não informado'}</strong><small>{item.comarca || 'Comarca não informada'}</small></td>
              <td><span className="portal-status">{item.status || item.situacao || 'Não informado'}</span></td>
              <td>{item.perspectivaPerda || 'Não informada'}</td>
              <td><strong>{formatReportDate(item.dataUltimoAndamento)}</strong></td>
            </tr>
          )) : <tr><td colSpan={6}><ReportEmptyState title="Nenhum processo encontrado" description="Tente ajustar o estado, a comarca ou sua busca." /></td></tr>}
        </tbody></table></div>
      )}
      {!loading && !error && page?.totalPaginas > 1 ? <div className="portal-pagination"><span>{formatReportNumber(page.total)} processos · página {page.pagina} de {page.totalPaginas}</span><div><button type="button" disabled={page.pagina <= 1} onClick={() => onPageChange(page.pagina - 1)}>Anterior</button><button type="button" disabled={page.pagina >= page.totalPaginas} onClick={() => onPageChange(page.pagina + 1)}>Próxima</button></div></div> : null}
    </section>
  )
}

function PortalDashboard() {
  const { user, logout } = useAuth()
  const [data, setData] = useState(null)
  const [processPage, setProcessPage] = useState(null)
  const [draftFilters, setDraftFilters] = useState(EMPTY_PROCESS_FILTERS)
  const [filters, setFilters] = useState(EMPTY_PROCESS_FILTERS)
  const [processPageNumber, setProcessPageNumber] = useState(1)
  const [refreshVersion, setRefreshVersion] = useState(0)
  const [loading, setLoading] = useState(true)
  const [processLoading, setProcessLoading] = useState(true)
  const [error, setError] = useState('')
  const [processError, setProcessError] = useState('')
  const [exportError, setExportError] = useState('')
  const [accessDenied, setAccessDenied] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [section, setSection] = useState('overview')
  const [selectedProcessId, setSelectedProcessId] = useState(null)
  const [openRequestCount, setOpenRequestCount] = useState(0)

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    getPortalDashboard(filters).then((result) => {
      if (!active) return
      if (result.success) setData(result.data)
      else {
        setAccessDenied(result.status === 403)
        setError(result.error || 'Não foi possível carregar seu portal.')
      }
      setLoading(false)
    })
    return () => { active = false }
  }, [filters, refreshVersion])

  useEffect(() => {
    let active = true
    setProcessLoading(true)
    setProcessError('')
    getPortalProcesses(filters, { page: processPageNumber, search: filters.busca }).then((result) => {
      if (!active) return
      if (result.success) {
        const lastPage = Math.max(1, result.data?.totalPaginas || 1)
        if (processPageNumber > lastPage) {
          setProcessPageNumber(lastPage)
          return
        }
        setProcessPage(result.data)
      } else setProcessError(result.error || 'Não foi possível carregar seus processos.')
      setProcessLoading(false)
    })
    return () => { active = false }
  }, [filters, processPageNumber, refreshVersion])

  useEffect(() => {
    let active = true
    getPortalClientRequests('OPEN').then((result) => {
      if (active && result.success) setOpenRequestCount(Array.isArray(result.data) ? result.data.length : 0)
    })
    return () => { active = false }
  }, [refreshVersion])

  const dashboard = data?.dashboard
  const client = data?.cliente
  const trendData = useMemo(() => ({
    labels: (dashboard?.tendenciaMensal || []).map((item) => { const [year, month] = String(item.competencia).split('-'); return `${month}/${year}` }),
    datasets: [
      { label: 'Entradas', data: (dashboard?.tendenciaMensal || []).map((item) => item.entradas), borderColor: CHART_THEME.linePrimary, backgroundColor: CHART_THEME.linePrimaryFill, fill: true, tension: .3 },
      { label: 'Encerramentos', data: (dashboard?.tendenciaMensal || []).map((item) => item.encerramentos), borderColor: CHART_THEME.lineSecondary, backgroundColor: CHART_THEME.lineSecondaryFill, tension: .3 },
    ],
  }), [dashboard])
  const areaData = useMemo(() => ({ labels: (dashboard?.porArea || []).slice(0, 8).map((item) => item.nome), datasets: [{ label: 'Processos', data: (dashboard?.porArea || []).slice(0, 8).map((item) => item.total), backgroundColor: VISUAL_COLORS.emerald, borderRadius: 1, borderSkipped: false }] }), [dashboard])
  const riskData = useMemo(() => ({ labels: (dashboard?.porPerspectivaPerda || []).map((item) => item.nome), datasets: [{ label: 'Processos', data: (dashboard?.porPerspectivaPerda || []).map((item) => item.total), backgroundColor: [...DATA_SERIES_COLORS.slice(0, 5)] }] }), [dashboard])
  const horizontalOptions = useMemo(() => ({ indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { precision: 0 } }, y: { grid: { display: false } } } }), [])

  const reload = () => setRefreshVersion((value) => value + 1)
  const applyFilters = (event) => {
    event.preventDefault()
    setFilters(Object.fromEntries(Object.entries(draftFilters).map(([key, value]) => [key, value.trim()])))
    setProcessPageNumber(1)
    setExportError('')
  }
  const clearFilters = () => {
    setDraftFilters(EMPTY_PROCESS_FILTERS)
    setFilters(EMPTY_PROCESS_FILTERS)
    setProcessPageNumber(1)
    setExportError('')
  }
  const updateDraftFilter = (field, value) => setDraftFilters((current) => ({ ...current, [field]: value, ...(field === 'estado' ? { comarca: '' } : {}) }))
  const filtersChanged = Object.keys(filters).some((key) => filters[key] !== draftFilters[key].trim())
  const appliedFilterDescription = [filters.estado, filters.comarca, filters.busca ? `Busca: ${filters.busca}` : ''].filter(Boolean).join(' · ')
  const exportReport = async () => {
    if (exporting) return
    setExporting(true)
    setExportError('')
    const result = await exportPortalReport(filters)
    setExporting(false)
    if (!result.success || !result.data) {
      setExportError(result.error || 'Não foi possível exportar seu relatório. Tente novamente.')
      return
    }
    const url = window.URL.createObjectURL(result.data)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = `relatorio-${String(client?.clienteNome || 'processos').replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}.xlsx`
    document.body.appendChild(anchor); anchor.click(); anchor.remove(); window.URL.revokeObjectURL(url)
  }

  if (!data && loading) return <main className="client-portal portal-centered-page"><div className="portal-page-loader"><PortalBrand /><i className="fas fa-spinner fa-spin" /><span>Preparando o Adjutorem IA...</span></div></main>
  if (accessDenied) return <PortalAccessDenied />
  if (!dashboard || !client) return <main className="client-portal portal-centered-page"><div className="portal-auth-card portal-denied-card"><PortalBrand compact /><h1>Não foi possível carregar seu portal</h1><p role="alert">{error || 'Os dados estão temporariamente indisponíveis.'}</p><button type="button" className="portal-primary-button" onClick={reload}>Tentar novamente</button><button type="button" className="portal-link-button" onClick={logout}>Sair desta conta</button></div></main>

  return (
    <main className="client-portal portal-app-page">
      <header className="portal-topbar">
        <PortalBrand />
        <nav className="portal-main-nav" aria-label="Navegação do portal">
          <div className="portal-main-nav-heading" aria-hidden="true"><span>01</span><strong>Adjutorem <em>IA</em></strong><i /></div>
          <button type="button" className={section === 'overview' ? 'active' : ''} onClick={() => setSection('overview')}><i className="fas fa-chart-pie" /><span><strong>Visão geral</strong><small>Indicadores da carteira</small></span></button>
          <button type="button" className={section === 'hearings' ? 'active' : ''} onClick={() => setSection('hearings')}><i className="far fa-calendar" /><span><strong>Pauta de audiências</strong><small>Agenda dos seus processos</small></span></button>
          <button type="button" className={section === 'requests' ? 'active' : ''} onClick={() => setSection('requests')}><i className="far fa-bell" /><span><strong>Pendências</strong><small>Solicitações em aberto</small></span>{openRequestCount ? <span className="badge">{openRequestCount}</span> : null}</button>
          <button type="button" className={section === 'documents' ? 'active' : ''} onClick={() => setSection('documents')}><i className="far fa-folder-open" /><span><strong>Documentos</strong><small>Arquivos compartilhados</small></span></button>
        </nav>
        <div className="portal-topbar-actions">
          <div className="portal-company"><span>Empresa</span><strong>{client.clienteNome}</strong></div>
          <ThemeToggle />
          <div className="portal-user"><span>{String(user?.nome || user?.name || 'Cliente').split(' ')[0]}</span><button type="button" onClick={logout} title="Sair" aria-label="Sair do portal"><i className="fas fa-arrow-right-from-bracket" /></button></div>
        </div>
      </header>

      <div className="portal-content portal-workspace-shell">
        {section === 'overview' ? <>
          <section className="portal-hero"><div><span className="portal-eyebrow">Adjutorem IA</span><h1>Visão geral</h1><p>Olá, {String(user?.nome || user?.name || 'cliente').split(' ')[0]}. Acompanhe os principais números e movimentações da sua carteira.</p></div><div className="portal-hero-actions"><button type="button" className="portal-secondary-button" onClick={reload} disabled={loading || processLoading}><i className={`fas ${loading || processLoading ? 'fa-spinner fa-spin' : 'fa-rotate-right'}`} /> Recarregar dados</button><button type="button" className="portal-export-button" onClick={exportReport} disabled={exporting || loading || processLoading || filtersChanged || !!error || !!processError}><i className={`fas ${exporting ? 'fa-spinner fa-spin' : 'fa-arrow-down'}`} /> {exporting ? 'Gerando...' : 'Exportar relatório'}</button></div></section>
          <section className="portal-section portal-filter-section" aria-label="Filtros da carteira">
            <form className="portal-filter-bar" onSubmit={applyFilters}>
              <PortalRegionFilters estado={draftFilters.estado} comarca={draftFilters.comarca} onChange={updateDraftFilter} refreshVersion={refreshVersion} />
              <label className="portal-field portal-filter-search"><span>Processo, parte ou objeto</span><input value={draftFilters.busca} onChange={(event) => updateDraftFilter('busca', event.target.value)} placeholder="Número do processo ou termo de busca" maxLength={200} /></label>
              <div className="portal-filter-actions"><button type="submit" className="portal-primary-button" disabled={loading || processLoading}>Aplicar filtros</button><button type="button" className="portal-secondary-button" onClick={clearFilters} disabled={loading || processLoading}>Limpar</button></div>
            </form>
            <p className="portal-filter-note">{filtersChanged ? 'Aplique os filtros para atualizar os indicadores, a lista e a exportação.' : appliedFilterDescription ? `Filtros aplicados aos indicadores, processos e relatório: ${appliedFilterDescription}` : 'Indicadores, processos e relatório de toda a sua carteira.'}</p>
            <p className="portal-data-updated"><i className="far fa-clock" /> Base dos processos atualizada em {formatPortalDateTime(dashboard.atualizadoEm)}</p>
          </section>
          {exportError ? <div className="portal-form-error" role="alert">{exportError}</div> : null}
          {loading ? <div className="portal-inline-loading" role="status"><i className="fas fa-spinner fa-spin" /> Atualizando indicadores...</div> : error ? <div className="portal-form-error portal-retry-error" role="alert"><span>{error}</span><button type="button" className="portal-secondary-button" onClick={reload}>Tentar novamente</button></div> : <>
          <section className="portal-metrics"><ReportStat index="01" label="Total de processos" value={formatReportNumber(dashboard.total)} icon="fa-scale-balanced" /><ReportStat index="02" label="Em andamento" value={formatReportNumber(dashboard.emAndamento)} detail={dashboard.total ? `${Math.round(dashboard.emAndamento * 100 / dashboard.total)}% da carteira` : ''} icon="fa-hourglass-half" tone="blue" /><ReportStat index="03" label="Encerrados" value={formatReportNumber(dashboard.encerrados)} icon="fa-circle-check" tone="green" /><ReportStat index="04" label="Arquivados" value={formatReportNumber(dashboard.arquivados)} icon="fa-box-archive" tone="purple" /></section>
          <section className="portal-financial-summary"><div><span>Provisão possível</span><strong>{formatReportCurrency(dashboard.valorProvisaoPossivel)}</strong></div><div><span>Provisão remota</span><strong>{formatReportCurrency(dashboard.valorProvisaoRemota)}</strong></div><div><span>Provisão provável</span><strong>{formatReportCurrency(dashboard.valorProvisaoProvavel)}</strong></div></section>
          <section className="portal-charts"><ReportChartCard eyebrow="Evolução da carteira" title="Entradas e encerramentos" type="line" data={trendData} className="portal-chart-wide" /><ReportChartCard eyebrow="Risco" title="Perspectiva de perda" type="doughnut" data={riskData} /><ReportChartCard eyebrow="Distribuição" title="Processos por área" type="bar" data={areaData} options={horizontalOptions} /></section>
          </>}
          <PortalProcesses page={processPage} loading={processLoading} error={processError} onRetry={reload} onPageChange={setProcessPageNumber} onProcessSelect={setSelectedProcessId} />
        </> : null}
        {section === 'hearings' ? <PortalHearingsPanel onProcessSelect={setSelectedProcessId} /> : null}
        {section === 'requests' ? <PortalClientRequestsPanel onOpenCountChange={setOpenRequestCount} /> : null}
        {section === 'documents' ? <PortalDocumentsPanel /> : null}
      </div>
      <PortalProcessDetailDrawer processoId={selectedProcessId} onClose={() => setSelectedProcessId(null)} />
    </main>
  )
}

export default function PortalApp() {
  const { isAuthenticated, user } = useAuth()
  const role = String(user?.role || '').trim().toUpperCase()
  useEffect(() => {
    const previousTitle = document.title
    document.title = `${APP_CONFIG.PORTAL_APP_NAME} | Veiga Advogados`
    return () => { document.title = previousTitle }
  }, [])

  if (!isAuthenticated) return <PortalLogin />
  if (role !== 'CLIENT') return <PortalAccessDenied />
  return <PortalDashboard />
}
